import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import { ACETable } from '@/app/common/components/Table/ACETable'
import {
  useAddProvidersToAffiliation,
  useAvailableProvidersForAffiliation,
} from '@/queries/providers/affiliations/hooks.ts'
import { AffiliationProvider } from '@/queries/providers/affiliations/schema.ts'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shad/ui/dialog.tsx'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  affiliationId: number
  affiliationName: string
  programId: number
}

const columnHelper = createColumnHelper<AffiliationProvider>()

type AddProvidersDialogColumnActions = {
  selectedProviderIds: number[]
  onToggleProvider: (providerId: number) => void
}

const createAddProvidersColumns = ({
  selectedProviderIds,
  onToggleProvider,
}: AddProvidersDialogColumnActions) => [
  columnHelper.accessor('id', {
    header: () => <div className="w-16">ID</div>,
    cell: (info) => <div className="w-16">{info.getValue()}</div>,
  }),
  columnHelper.accessor('name', {
    header: () => <div>Name</div>,
    cell: (info) => (
      <div className="truncate" title={info.getValue() ?? undefined}>
        {info.getValue()}
      </div>
    ),
  }),
  columnHelper.display({
    id: 'action',
    header: () => <div className="w-36 text-center">Action</div>,
    cell: (info) => {
      const row = info.row.original
      const isSelected = selectedProviderIds.includes(row.id)
      const disableSelection = !isSelected && selectedProviderIds.length >= 10

      return (
        <div className="flex w-36 justify-center">
          <Button
            type="button"
            variant={isSelected ? 'outline' : 'primary'}
            size="xs"
            className="w-32"
            disabled={disableSelection}
            onClick={() => {
              onToggleProvider(row.id)
            }}
          >
            {isSelected ? 'Selected' : 'Select Provider'}
          </Button>
        </div>
      )
    },
  }),
]

export default function AddProvidersDialog({
  open,
  onOpenChange,
  affiliationId,
  affiliationName,
  programId,
}: Props) {
  const { toast } = useToast()
  const aceTable = useAceTable()
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([])

  const availableProvidersQuery = useAvailableProvidersForAffiliation({
    affiliationId,
    programId,
    currentPage: aceTable.currentPage,
    recordsPerPage: aceTable.recordsPerPage,
    search: aceTable.debouncedSearch.trim() || undefined,
  })

  const addProvidersMutation = useAddProvidersToAffiliation({
    onSuccess: (result) => {
      toast({
        title: 'Providers Added',
        description: `${result.inserted_count} provider(s) were added to ${affiliationName}.`,
        variant: 'default',
      })
      setSelectedProviderIds([])
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const toggleProvider = (providerId: number) => {
    setSelectedProviderIds((previous) => {
      if (previous.includes(providerId)) {
        return previous.filter((id) => id !== providerId)
      }

      if (previous.length >= 10) {
        return previous
      }

      return [...previous, providerId]
    })
  }

  const columns = createAddProvidersColumns({
    selectedProviderIds,
    onToggleProvider: toggleProvider,
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (addProvidersMutation.isPending && !nextOpen) {
          return
        }
        if (!nextOpen) {
          setSelectedProviderIds([])
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add Providers</DialogTitle>
          <DialogDescription>
            Select up to 10 providers to add to {affiliationName}.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-auto [&_table]:w-full [&_table]:table-fixed">
          <ACETable<AffiliationProvider>
            data={availableProvidersQuery.data?.data ?? []}
            columns={columns}
            totalRows={availableProvidersQuery.data?.total ?? 0}
            currentPage={aceTable.currentPage}
            recordsPerPage={aceTable.recordsPerPage}
            search={aceTable.search}
            onSearchChange={aceTable.onSearchChange}
            onPageChange={aceTable.onPageChange}
            onPerPageChange={aceTable.onPerPageChange}
            onRefresh={() => {
              void availableProvidersQuery.refetch()
            }}
            title="Available Providers"
            searchPlaceholder="Search Available Providers"
            emptyMessage="No available providers found."
            status={availableProvidersQuery.status}
            filter={<div />}
          />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="text-sm text-gray-600">
            {selectedProviderIds.length} selected
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={addProvidersMutation.isPending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>

            <div>
              <SubmitButton
                type="button"
                status={addProvidersMutation.status}
                buttonTxt="Add Providers"
                submittingText="Submitting..."
                forceDisabled={selectedProviderIds.length === 0}
                onClick={() => {
                  addProvidersMutation.mutate({
                    affiliationId,
                    programId,
                    providerIds: selectedProviderIds,
                  })
                }}
              />
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
